import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocalizedStrings } from "../Localization";
import { ConfigurationContext } from "../Context";
import { usePost } from "../API";
import { safeParse, SpacedSpinner } from "../Util";
import { Card, CardHeader, CardBody, CardFooter, Button, Icon, CodeDisplay, DataDisplayProvider, Switch, DateDisplay, Input } from "@aidkitorg/component-library";
import { HistoryItem } from "@aidkitorg/aidkit/lib/application/history";
import { AppInfoMetadata } from "@aidkitorg/types/lib/survey";

type HistoryItemProps = {
  item: HistoryItem;
  config: Record<string, any>;
  searchTerm: string;
};

function HistoryItemComponent(props: HistoryItemProps) {
  const L = useLocalizedStrings();
  const [popoverVisible, setPopoverVisible] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const popParentRef = useRef<HTMLDivElement | null>(null);
  const [prettyValue, setPrettyValue] = useState('');
  const [isJSON, setIsJSON] = useState(false);

  function highlightText(text?: string, searchTerm?: string): JSX.Element {
    if (!text) return <></>;
    if (!searchTerm) return <>{text}</>;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? <span key={index} className="bg-yellow-100">{part}</span> : part
        )}
      </>
    );
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target && !target.closest('.metadata-popover') && !target.closest('.popover-button')) {
        setPopoverVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (popoverVisible && popoverRef.current && popParentRef.current) {
      const popover = popoverRef.current;
      const popParent = popParentRef.current;
      const popoverRect = popover.getBoundingClientRect();
      const parentRect = popParent.getBoundingClientRect();

      const offset = parentRect.left - parentRect.left;

      if (popoverRect.right > parentRect.right) {
        popover.style.right = `${offset - (popoverRect.right - parentRect.right) - 10}px`;
      } else if (popoverRect.left < parentRect.left) {
        popover.style.right = `${offset + (parentRect.left - popoverRect.left) + 10}px`;
      } else {
        popover.style.right = `${offset}px`;
      }
      popover.style.top = '-25px'
    }
  }, [popoverVisible]);


  useEffect(() => {
    try {
      if (isNaN(Number(props.item.value))) {
        const parsed = JSON.stringify(JSON.parse(props.item.value), null, 2);
        setIsJSON(true)
        setPrettyValue(parsed);
      } else {
        setPrettyValue(props.item.value)
        setIsJSON(false)
      }
    } catch (e) {
      setIsJSON(false);
      setPrettyValue(props.item.value);
    }
  }, [props.item.value]);

  const parsedMetadata: AppInfoMetadata = useMemo(() => {
    return safeParse(props.item.metadata);
  }, [props.item.metadata]);

  return (
    <Card className="p-3">
      <CardHeader
        title={<strong>{highlightText(props.item.key, props.searchTerm)}</strong>}
        badgeText={!!props.item.latest ? L.applicant.history.latest : undefined}
        badgeVariant="success"
        actionButton={
          <Button
            aria-label="Show metadata"
            className="popover-button px-0"
            variant="unstyled"
            icon={<Icon
              name="InformationCircleIcon"
              role="decorative"
            />}
            onClick={() => { setPopoverVisible(!popoverVisible) }}
          >
          </Button>
        }
      />
      <CardBody className="py-2">
        <>
          <div ref={popParentRef} >
            {props.config?.roles?.includes('admin') && (
              <div className="relative">
                {popoverVisible && (
                  <div ref={popoverRef} className="absolute z-10 p-2 mt-2 w-72 bg-white border border-gray-200 rounded-md shadow-lg metadata-popover">
                    <div className="p-2 text-sm font-medium text-gray-700">
                      {L.campaigns.metadata}
                    </div>
                    <pre className="bg-gray-100 p-2 max-h-80 overflow-y-auto text-sm text-gray-700 text-monospace">
                      {props.item.metadata}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <CodeDisplay
              className='border-0'
              variant="gray"
              contentMode="wrap"
              language={isJSON ? "json" : "text"}
              code={prettyValue}
              highlightText={props.searchTerm}
              copyable />
          </div>
        </>
      </CardBody>
      <CardFooter>
        <div className="text-sm flex justify-end flex-col items-end">
          <div>
            {parsedMetadata?.source && <span>{parsedMetadata.source + ' - '}</span>}
            {highlightText(props.item.author_name, props.searchTerm)}
          </div>
          <div>
            <DateDisplay variant='datetime' utcValue={props.item.created_at} />
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

type HistoryProps = { uid: string, canViewChanges: boolean };

export default function History(props: HistoryProps) {
  const L = useLocalizedStrings();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [exactMatch, setExactMatch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isUpdating = useRef(false);
  const config = useContext(ConfigurationContext);
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [searchTargetField, setSearchTargetField] = useState(true);
  const [searchValue, setSearchValue] = useState(true);
  const [searchAuthor, setSearchAuthor] = useState(true);
  const [onlyShowLatest, setOnlyShowLatest] = useState(false);
  const [hideEmptyErrors, setHideEmptyErrors] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const getHistory = usePost('/applicant/get_history');

  type NextParams = NonNullable<(Awaited<ReturnType<typeof getHistory>>['nextParams'])>;

  const [nextParams, setNextParams] = useState<NextParams | undefined>();
  const [history, setHistory] = useState<Map<number, HistoryItem>>(new Map());
  const [filteredHistory, setFilteredHistory] = useState<HistoryItem[]>([]);

  const [useUTC, setUseUTC] = useState(false);
  const onCheckedChange = useCallback((checked: boolean) => setUseUTC(checked), []);

  const showMoreRef = useRef(null);

  const searchHistory = useCallback(async (options?: { nextParams?: typeof nextParams, offset?: number, limit?: number }) => {
    isUpdating.current = true;
    setIsLoading(true);
    const fetchedHistory = await getHistory(options?.nextParams ?? {
      applicant: props.uid,
      substring_search_key: searchTargetField ? searchTerm.trim() : undefined,
      substring_search_author: searchAuthor ? searchTerm.trim() : undefined,
      substring_search_value: searchValue ? searchTerm.trim() : undefined,
      exact: exactMatch,
      latest: onlyShowLatest ? true : undefined,
      start_date: startDate ? new Date(startDate).toISOString() : undefined,
      end_date: endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString() : undefined,
      offset: options?.offset,
      limit: options?.limit
    });
    if (fetchedHistory && !('error' in fetchedHistory)) {
      if (options?.nextParams) {
        setHistory(
          history => {
            for (const item of fetchedHistory.changes) {
              history.set(item.id, item);
            }  
            return history;    
          }
        )
      }
      else {
        setHistory(new Map(fetchedHistory.changes.map(item => [item.id, item])));
      }
      setNextParams(fetchedHistory.nextParams);
    }
    setIsLoading(false);  
    isUpdating.current = false;
  }, [props.uid, searchTargetField, searchAuthor, searchValue, searchTerm, exactMatch, onlyShowLatest, startDate, endDate]);

  // debounce the search input
  useEffect(() => {
    const loadTimeout = setTimeout(
      () => {
        if (!searchInput || searchInput.length > 2) {
          setSearchTerm(searchInput);
        }
      },
      200
    );  
    return () => {
      clearTimeout(loadTimeout);
    }
  }, [searchInput]);

  // refetch on all other changes
  useEffect(() => {
    searchHistory();
  }, [searchHistory]);

  // polling interval
  useEffect(() => {
    const pollingInterval = setInterval(
      () => {
        searchHistory({ offset: 0, limit: Math.max(history.size, 50) });
      },
      5000
    );
    return () => {
      clearInterval(pollingInterval);
    }
  }, [searchHistory, history.size])

  // clear empty errors on the client side
  useEffect(() => {
    if (history) {
      // This keeps track of fields which end in _error and have never had a non-empty value
      const alwaysEmptyErrors: Record<string, boolean> = {};
      if (hideEmptyErrors) {
        for (const item of Array.from(history.values()) as HistoryItem[]) {
          if (item.key.endsWith('_error')) {
            if (item.value) {
              alwaysEmptyErrors[item.key] = false;
            } else {
              // Only overwrite undefined values
              alwaysEmptyErrors[item.key] ??= true;
            }
          }  
        }
      }

      // If we want to hide always empty errors, then refilter with the map we constructed above.
      // !== true will cover undefined, and false
      const incomingHistory = Array.from(history.values());
      setFilteredHistory(
        hideEmptyErrors
          ? incomingHistory.filter((historyItem:HistoryItem) => alwaysEmptyErrors[historyItem.key] !== true)
          : incomingHistory
      );
    }
  }, [history, history.size, hideEmptyErrors]);

  useEffect(() => {
    // Show more ref to render more results when a user scrolls to the bottom of the list.
    // debounce this event
    let debounceTimeout : NodeJS.Timeout | undefined;
    const observer = new IntersectionObserver((entries) => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
        debounceTimeout = undefined;
      }
      debounceTimeout = setTimeout(
        () => {
          entries.forEach(entry => {            
            if (entry.isIntersecting && entry.target === showMoreRef.current && history.size && nextParams) {
              searchHistory({ nextParams });
              observer.unobserve(showMoreRef.current);
            }
          });    
        },
        200
      );
    });

    if (showMoreRef.current) {
      observer.observe(showMoreRef.current);
    }

    return () => {
      if (showMoreRef.current) {
        observer.unobserve(showMoreRef.current);
      }
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [nextParams, showMoreRef.current, history.size]);

  return (
    <div>
      <div className="mb-4">
        <div className="flex-grow mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {L.dashboard.search}
          </label>
          <div className="flex items-center relative">
            <input
              type="text"
              className="flex-grow p-1 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder={L.dashboard.search}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchTerm && (
              <Button
                className="absolute !p-0 right-8"
                aria-label="Clear search terms"
                variant="unstyled"
                icon={
                  <Icon
                    name="XCircleIcon"
                    role="decorative"
                  />
                }
                onClick={() => { setSearchInput(''); setSearchTerm(''); }
                }
              />
            )}
            <Button
              className="!p-0.5"
              variant="unstyled"
              aria-label="Filter"
              icon={
                <Icon
                  name="FunnelIcon"
                  role="decorative"
                />
              }
              onClick={() => setShowSearchOptions(!showSearchOptions)}
            />
          </div>
        </div>

        {showSearchOptions && <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input
              type="date"
              label={L.applicant.history.start_date}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full"
            />
            <Input
              type="date"
              label={L.applicant.history.end_date}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div className="flex items-center cursor-pointer">
              <input
                id="exactMatch"
                type="checkbox"
                className="h-4 w-4 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                checked={exactMatch}
                onChange={() => setExactMatch(!exactMatch)}
              />
              <label htmlFor="exactMatch" className="ml-2 mb-0  text-gray-700 cursor-pointer">
                {L.applicant.history.exact_match}
              </label>
            </div>
            <div className="flex items-center cursor-pointer">
              <input
                id="searchValue"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                checked={searchValue}
                onChange={() => setSearchValue(!searchValue)}
              />
              <label htmlFor="searchValue" className="ml-2 mb-0  text-gray-700 cursor-pointer">
                {L.applicant.history.search_values}
              </label>
            </div>
            <div className="flex items-center cursor-pointer">
              <input
                id="searchTargetField"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                checked={searchTargetField}
                onChange={() => setSearchTargetField(!searchTargetField)}
              />
              <label htmlFor="searchTargetField" className="ml-2 mb-0  text-gray-700 cursor-pointer">
                {L.applicant.history.search_target_fields}
              </label>
            </div>
            <div className="flex items-center cursor-pointer">
              <input
                id="searchAuthor"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                checked={searchAuthor}
                onChange={() => setSearchAuthor(!searchAuthor)}
              />
              <label htmlFor="searchAuthor" className="ml-2 mb-0  text-gray-700 cursor-pointer">
                {L.applicant.history.search_authors}
              </label>
            </div>
            <div className="flex items-center cursor-pointer">
              <input
                id="onlyShowLatest"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                checked={onlyShowLatest}
                onChange={() => setOnlyShowLatest(!onlyShowLatest)}
              />
              <label htmlFor="onlyShowLatest" className="ml-2 mb-0 text-gray-700 cursor-pointer">
                {L.applicant.history.only_show_latest}
              </label>
            </div>
            <div className="flex items-center cursor-pointer">
              <input
                id="hideEmptyErrors"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                checked={hideEmptyErrors}
                onChange={() => setHideEmptyErrors(!hideEmptyErrors)}
              />
              <label htmlFor="hideEmptyErrors" className="ml-2 mb-0 text-gray-700 cursor-pointer">
                {L.applicant.history.hide_empty_errors}
              </label>
            </div>
          </div>
        </div>}
      </div>
      <div className="overflow-hidden">
        <ul className="divide-y divide-gray-200">
          <DataDisplayProvider value={{ displayInUTC: useUTC }}>
            <div className='px-1 py-2'>
              <Switch label="Display time in UTC" onCheckedChange={onCheckedChange} />
            </div>
            {filteredHistory.map((item: HistoryItem, i: number) => (
              <HistoryItemComponent key={`history_${item.id}_${i}`} item={item} config={config} searchTerm={searchTerm} />
            ))}
          </DataDisplayProvider>
        </ul>
        {(nextParams && isLoading) && <div className="flex justify-center">
          <SpacedSpinner className="text-gray-500" />
        </div>}
        { (nextParams && history.size > 0) && <div ref={showMoreRef} /> }
      </div>
    </div>
  );
}
